/* eslint-disable */

import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Form, Spin } from 'antd';
import { Box } from '@mui/material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useLocation } from 'react-router-dom';
import CardAccordion from '../CardAccordion';
import LeaseTable from '../../leaseRecodTable';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useSelector } from 'react-redux';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { utcConverter } from '../LeaseUtils/helper';

dayjs.extend(utc);

const LeaseSchedule = forwardRef(({ screen, viewData, dataFullyLoaded = false }, ref) => {
	const form = Form.useFormInstance();
	const [hasLoadedData, setHasLoadedData] = useState(false);
	const [getLeaseTableData, { data: leaseControlDataV3, isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();
	const [getLeaseData, { data: leaseControlData, isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();
	const [flag, setFlag] = useState({
		check: 0,
		editable: 0,
	});

	const { state } = useLocation();

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	const queryV3Loader = async (force = false) => {
		if (screen !== 'CREATE' && (!hasLoadedData || force)) {
			const version = String(viewData?.data?.version);

			if (
				version &&
				companyCode &&
				(viewData?.activationStatus?.toLowerCase() === 'active' ||
					viewData?.activationStatus?.toLowerCase() === 'closed' ||
					viewData?.activationStatus?.toLowerCase() === 're-open')
			) {
				await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, version],
					name: 'leaseSchedulesOnVersion-v8',
				});
			}
			setHasLoadedData(true);
		}
	};
	const memoizedLeaseControlData = useMemo(() => {
		if (screen?.toLowerCase() === 'view' || flag?.check === 0) {
			return leaseControlDataV3?.results ?? [];
		}

		return leaseControlData?.responseData ?? [];
	}, [flag?.check, leaseControlDataV3?.results, leaseControlData?.responseData, queryV3Loader]);

	const handleDataLoading = async () => {
		let payloadData = {};
		const formData = form.getFieldsValue();

		if (screen?.toLowerCase() === 'create') {
			payloadData = {
				advanceAmount: formData?.advanceAmount,
				retirementTerm: formData?.retirementTerm,
				discountRate: formData?.initialDiscountRate,
				escalation: 0,
				startYear: String(formData?.changes[0]?.inputChangeEffectiveDate?.year()),
				startMonth: String(formData?.changes[0]?.inputChangeEffectiveDate?.month() + 1),
				startDay: String(formData?.changes[0]?.inputChangeEffectiveDate?.date()),
				paymentFrequency: formData?.paymentFrequency,
				paymentTime: formData?.paymentModality,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: formData?.upfrontLeasePayment,
				initialDirectCost: formData?.initialDirectCost,
				securityDeposit: formData?.securityDeposit,
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.advanceAmounts,
				grossRents: formData?.grossRents,
				retirementCost: formData?.retirementCost,
				grossRent: 0,
				changes: [],
				cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				inputChangeMonth: 1,
			};
			// console.log('🚀 ~ handleDataLoading ~ payloadData:', payloadData);
			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};
			try {
				await getLeaseData(formattedPayload).unwrap();
			} catch (error) {
				return;
			}
		} else {
			if (dataFullyLoaded) {

				const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);

				// Normalize array lengths by ensuring inputChangeAdvanceAmounts has same length as inputChangeGrossRents
				const normalizedEffectiveChanges = effectiveChanges.map((item) => {
					const grossRentsLength = item?.inputChangeGrossRents?.length || 0;
					const advanceAmountsLength = item?.inputChangeAdvanceAmounts?.length || 0;

					// If advanceAmounts array is shorter than grossRents array, pad with zeros
					if (grossRentsLength > advanceAmountsLength && Array.isArray(item?.inputChangeGrossRents)) {
						const paddedAdvanceAmounts = [...(item?.inputChangeAdvanceAmounts || [])];
						// Add zeros to match the length
						while (paddedAdvanceAmounts.length < grossRentsLength) {
							paddedAdvanceAmounts.push(0);
						}
						return {
							...item,
							inputChangeAdvanceAmounts: paddedAdvanceAmounts,
						};
					}
					return item;
				});

				payloadData = {
					advanceAmount: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce(
						(accumulator, currentValue) => accumulator + currentValue,
						0
					),
					retirementTerm: Math.abs(dayjs(formData?.changes?.[0]?.inputChangeEffectiveDate)?.diff(formData?.changes?.[0]?.inputChangeRetirementDate, 'month'))+1,
					discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
					startYear: String(formData?.changes[0]?.inputChangeEffectiveDate?.year()),
					startMonth: String(formData?.changes[0]?.inputChangeEffectiveDate?.month() + 1),
					startDay: String(formData?.changes[0]?.inputChangeEffectiveDate?.date()),
					escalation: 0,
					paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
					paymentTime: formData?.changes?.[0]?.paymentTime,
					advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
					upfrontLeasePayment: formData?.changes?.[0]?.upfrontLeasePayment,
					initialDirectCost: formData?.changes?.[0]?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					endOfFinancialYear: formData?.endOfFinancialYear,
					advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
					grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
					retirementCost: formData?.retirementCost,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					inputChangeMonth: 1,
					changes: normalizedEffectiveChanges.map((item, index) => {
						if (index === normalizedEffectiveChanges.length - 1) {
							return {
								...item,
								upfrontLeasePayment: formData?.upfrontLeasePayment,
								initialDirectCost: formData?.initialDirectCost,
								securityDeposit: formData?.securityDeposit,
								cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
								inputChangeEffectiveDate: utcConverter(dayjs(item?.inputChangeEffectiveDate)),
								inputChangeRetirementTerm:Math.abs(dayjs(item?.inputChangeEffectiveDate)?.diff(item?.inputChangeRetirementDate, 'month'))+1,
								inputChangeMonth: Math.abs(dayjs(formData?.changes[0]?.inputChangeEffectiveDate)?.diff(item?.inputChangeEffectiveDate, 'month'))+1,
							};
						}
						return {
							...item,
						};
					}),
					grossRent: 0,
				};

				if (
					Array.isArray(payloadData.grossRents) &&
					Array.isArray(payloadData.advanceAmounts) &&
					payloadData.grossRents.length > payloadData.advanceAmounts.length
				) {
					const paddedAdvanceAmounts = [...payloadData.advanceAmounts];
					while (paddedAdvanceAmounts.length < payloadData.grossRents.length) {
						paddedAdvanceAmounts.push(0);
					}
					payloadData.advanceAmounts = paddedAdvanceAmounts;
				}
			}

			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};

			try {
				await getLeaseData(formattedPayload).unwrap();
			} catch (error) {
				console.error('Error in data loading:', error);
			}
		}
	};
	useImperativeHandle(ref, () => ({
		handleDataLoading,
		flag,
		setFlag,
		queryV3Loader,
	}));

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', pt: '15px', flexDirection: 'column' }}>
			<Spin spinning={isLeaseControlDataLoading}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', pt: '15px', flexDirection: 'row' }}>
					{screen !== 'VIEW' && (
						<>
							{/* Left side buttons */}
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Button
									type="primary"
									onClick={() => {
										setFlag({ ...flag, check: 1 });
										handleDataLoading();
									}}
									style={{ marginBottom: 10, marginTop: 10 }}
									loading={isLeaseControlDataLoading}
								>
									{screen === 'CREATE' ? 'Run Decommission Asset Schedule' : 'Update Decommission Asset Schedule'}
								</Button>
							</Box>

							{/* Right side button */}
						</>
					)}
				</Box>
				<Spin spinning={isLeaseTableDataLoading || isLeaseControlDataLoading}>
					{memoizedLeaseControlData?.length > 0 && (
						<CardAccordion
							title="Lease Schedule"
							customHeaderSX={{
								fontSize: '16px',
								backgroundColor: '#e1f0ff !important',
								p: '10px',
							}}
							typographySX={{ fontSize: '16px !important' }}
							darkTitle={true}
						>
							<LeaseTable leaseControlData={memoizedLeaseControlData} screen={screen} queryV3Loader={queryV3Loader} />
						</CardAccordion>
					)}
				</Spin>
			</Spin>
		</Box>
	);
});

LeaseSchedule.displayName = 'LeaseSchedule';

export default LeaseSchedule;
